import { differenceInMinutes } from "date-fns";
import type { Break, BreakTimestamps } from "../types/Shift.types";

export type CompleteBreakTimestamps = BreakTimestamps & {
  start: NonNullable<BreakTimestamps['start']>;
  end: NonNullable<BreakTimestamps['end']>;
};

export const hasAnyOverlappingBreaks = (breaks: Array<Break>) =>
  !!breaks
    .flatMap((b1, idx) => breaks.slice(idx + 1).map((b2) => [b1, b2]), [])
    .find(([b1, b2]) => b1.start <= b2.end && b2.start <= b1.end);

export const hasAnyStartAfterEnd = (breaks: Array<Break>) => !!breaks.find((b) => b.start > b.end);

export const hasAnyBreakOutsideShift = (
  shiftStartDate: Date,
  shiftEndDate: Date,
  breaks: Array<Break>,
): boolean => !!breaks.find((b) => b.start < shiftStartDate || b.end > shiftEndDate);

export const hasAnyMissingBreaks = (breaks: Array<Partial<Break>>) =>
  !!breaks.find((b) => !(b.start && b.end));

export const getDurationInMinutes = (breaks: Array<Partial<Break>> | undefined) =>
  breaks?.reduce((total, breakObj) => {
    const interval =
      breakObj.start && breakObj.end ? differenceInMinutes(breakObj.end, breakObj.start) : 0;
    return total + interval;
  }, 0);

export const isCompleteBreakTimestamp = (breakObj: BreakTimestamps): breakObj is CompleteBreakTimestamps =>
  !!(breakObj.start?.time && breakObj.end?.time);

export const filterCompleteBreakTimestamps = (
  breaks: Array<BreakTimestamps> | undefined,
): Array<CompleteBreakTimestamps> => (breaks || []).filter(isCompleteBreakTimestamp);

export const getDisplayDurationInMinutes = (breakObj: Partial<Break>): string => {
  if (!breakObj.start || !breakObj.end) {
    return "-- min";
  }

  // Use the same logic as getDurationInMinutes for consistency
  const diffInMinutes = differenceInMinutes(breakObj.end, breakObj.start);

  if (diffInMinutes < 0) {
    return "-- min";
  }

  return `${diffInMinutes} min`;
};
